import { useState, useCallback, useEffect, useRef } from 'react';

import { SimpleSwitch, SimpleButton, TTSButton, Loading, ErrorMessage } from '@/src/ui';
import type { QtiOpenTextQuestionDTO } from '../../models';
import {
    DynamicEquationEditor,
    DynamicTextPreview,
    isEquationEditorEmpty,
    type QuestionRendererOptions,
    defaultOptions,
} from '@/src/lib';
import { Constants } from '@/src/utils';
import { submitForGrading } from '../../services';

interface OpenTextRendererProps {
    question: QtiOpenTextQuestionDTO;
    handleUpdateUserResponse?: (response: string, didUserInteract: boolean) => void;
    loadedUserResponse?: string;
    options?: QuestionRendererOptions;
}

export default function OpenTextRenderer({
    question,
    handleUpdateUserResponse,
    loadedUserResponse,
    options = defaultOptions,
}: OpenTextRendererProps) {
    const {
        disabled = false,
        hidePrompt = false,
        hideKeypad = false,
        showAudioButtons = false,
        showLanguageToggle = false,
        showScoring = false,
        language = 'en',
    } = options;

    const [error, setError] = useState<string | null>(null);
    const [inputText, setInputText] = useState(Constants.DEE_DEFAULT_TEXT);

    const hasUserResponseLoaded = useRef(false);

    // Language may need to be hanldled internally if it is not controlled from the outside
    const [internalLanguage, setInternalLanguage] = useState<'en' | 'es'>(language);
    useEffect(() => {
        setInternalLanguage(language);
    }, [language]);

    // Use if scoring needs to be handled internally
    const [userResponse, setUserResponse] = useState('');
    if (!handleUpdateUserResponse) {
        handleUpdateUserResponse = setUserResponse;
    }

    // Manage save data
    const userInteractedRef = useRef(false);
    useEffect(() => {
        if (loadedUserResponse) {
            const parsed = JSON.parse(loadedUserResponse);
            if (parsed.text) {
                setInputText(parsed.text);
            }
        }
        hasUserResponseLoaded.current = true;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        handleUpdateUserResponse(JSON.stringify({ text: inputText }), userInteractedRef.current);
        userInteractedRef.current = false;

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inputText]);

    return (
        <>
            {showLanguageToggle && question.meta.isSpanishAllowed && (
                <div className="flex flex-row gap-x-8">
                    <div className="ml-auto">
                        <SimpleSwitch
                            name="toggleSpanish"
                            checked={internalLanguage === 'es'}
                            onChange={(e) => setInternalLanguage(e.target.checked ? 'es' : 'en')}
                            label="View Spanish"
                        />
                    </div>
                </div>
            )}
            {!hidePrompt && (
                <div
                    className="mb-4 flex flex-row items-end"
                    onContextMenu={(e) => {
                        if (options.onRendererContextMenu) {
                            e.preventDefault();
                            e.stopPropagation();
                            options.onRendererContextMenu(
                                e,
                                {
                                    language: internalLanguage,
                                    text: question.prompt,
                                    text_es: question.prompt_es,
                                },
                                []
                            );
                        }
                    }}
                >
                    <DynamicTextPreview
                        content={internalLanguage === 'en' ? question.prompt : question.prompt_es}
                    />
                    {showAudioButtons && (
                        <TTSButton
                            text={internalLanguage === 'en' ? question.prompt : question.prompt_es}
                            onError={setError}
                        />
                    )}
                </div>
            )}
            <div>
                <DynamicEquationEditor
                    initialValue={inputText}
                    setOutput={(output: string) => {
                        setInputText(output);
                        userInteractedRef.current = true;
                    }}
                    contentKey={hasUserResponseLoaded.current ? `editor-${question.id}` : 'initial'}
                    showKeypad={!hideKeypad && question.meta.shouldShowKeypad}
                    allowedKeys={question.meta.keypadSetting}
                    style={{ width: '100%' }}
                    disabled={disabled}
                />
                {/* LIMIT 2000 because quotes get escaped & db limit is 3000 */}
                {inputText.length > 2000 && (
                    <ErrorMessage color="blue" error="See an error? Your answer may be too long." />
                )}
            </div>

            <OpenTextScoring
                question={question}
                userResponse={userResponse}
                showScoring={showScoring}
            />
            <ErrorMessage error={error} />
        </>
    );
}

interface OpenTextScoringProps {
    question: QtiOpenTextQuestionDTO;
    userResponse: string;
    showScoring: boolean;
}

function OpenTextScoring({ question, userResponse, showScoring }: OpenTextScoringProps) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [isCorrect, setIsCorrect] = useState(false);
    const [answerChecked, setAnswerChecked] = useState(false);
    useEffect(() => {
        setAnswerChecked(false);
    }, [userResponse]);

    const parsedResponse = userResponse ? JSON.parse(userResponse) : null;
    const userAnswer = parsedResponse?.text || '';

    const handleCheckAnswer = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const { isCorrect: isDeterminedCorrect } = await submitForGrading(
                question,
                userResponse
            );
            setIsCorrect(isDeterminedCorrect);
            setAnswerChecked(true);
        } catch {
            setError('Failed to check answer.');
            setIsCorrect(false);
        } finally {
            setLoading(false);
        }
    }, [question, userResponse]);

    const isAnswered = !isEquationEditorEmpty(userAnswer);

    if (!showScoring || question.meta.isHandScored === true) return null;

    return (
        <div className="mt-4 flex items-center space-x-4">
            <div>
                <SimpleButton
                    color="success"
                    onClick={handleCheckAnswer}
                    disabled={!isAnswered || answerChecked || loading}
                    variant="contained"
                    className="px-4 py-1 rounded disabled:opacity-50"
                >
                    Check Answer
                </SimpleButton>
                {loading && <Loading message="Checking answer..." />}
            </div>

            {error ? (
                <ErrorMessage error={error} />
            ) : (
                <span
                    className={`${isCorrect ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}`}
                >
                    {answerChecked && isAnswered
                        ? isCorrect
                            ? 'Correct!'
                            : 'Incorrect'
                        : '\u00A0'}
                </span>
            )}
        </div>
    );
}
